/**
 * A model id, typed rather than picked, with a way to find out if it works.
 *
 * Model versions change faster than migrations ship — the catalogue seeded two
 * migrations ago was already wrong three ids out of four — so the field is free
 * text. Two things will not save: a Gemini id outside the chat's allowlist
 * (src/config/geminiAllowlist.ts), and a provider and id whose last test failed.
 * A Claude or GPT typo still saves cleanly and takes the workspace's assistant
 * down hours later, which is why testing is next to the field rather than
 * somewhere else: the moment to find out is before saving, not when a user asks
 * a question and gets nothing.
 *
 * @module components/admin/ModelField
 */

import React, { useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { testModel } from "@/services/aiModelService";
import { isAllowedModel } from "@/config/geminiAllowlist";

const PROVIDERS = [
  { id: "gemini", label: "Gemini" },
  { id: "openai", label: "GPT" },
  { id: "anthropic", label: "Claude" },
] as const;

/** Same shape the database and the routes enforce. */
const MODEL_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,80}$/;

export interface ModelFieldProps {
  provider: string;
  model: string;
  /** Suggestions only — typing something absent from this list is expected. */
  suggestions: string[];
  /** null = never tested. */
  verifiedOk: boolean | null;
  busy: boolean;
  saveLabel: string;
  onSave: (provider: string, model: string, verifiedOk: boolean | null) => void;
  onClear?: () => void;
}

export const ModelField: React.FC<ModelFieldProps> = ({
  provider,
  model,
  suggestions,
  verifiedOk,
  busy,
  saveLabel,
  onSave,
  onClear,
}) => {
  const [draftProvider, setDraftProvider] = useState(provider);
  const [draftModel, setDraftModel] = useState(model);
  // useState seeds ONCE, and the admin table keys each row by workspace id, so
  // React reconciles rather than remounts when the underlying selection
  // changes — leaving a stale draft (and a stale test result vouching for it)
  // in a row that now describes a different model.
  useEffect(() => {
    setDraftProvider(provider);
    setDraftModel(model);
    setResult(null);
  }, [provider, model]);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const dirty = draftProvider !== provider || draftModel !== model;
  const valid = MODEL_RE.test(draftModel.trim());
  // What Save will not write, and why. The chat routes refuse every Gemini id
  // outside the allowlist, so saving one takes the assistant down; and a model
  // just seen failing is not one to run a workspace on. A verdict is dropped on
  // any edit to the provider or the id, so a failed one is about this pair.
  // Testing stays open: the test's refusal names the ids the chat accepts.
  const blockedBy =
    valid && draftProvider === "gemini" && !isAllowedModel(draftModel.trim())
      ? "แชทไม่รับ Gemini รุ่นนี้ จึงบันทึกไม่ได้"
      : result && !result.ok
        ? "ทดสอบไม่ผ่าน จึงบันทึกไม่ได้"
        : null;

  const runTest = async (): Promise<void> => {
    setTesting(true);
    setResult(null);
    try {
      setResult(await testModel(draftProvider, draftModel.trim()));
    } catch (e) {
      // Without this the spinner ran forever on a rejected promise — the
      // button stayed disabled and the master had no way to learn why, on the
      // one control whose entire job is telling them whether something works.
      setResult({
        ok: false,
        message: e instanceof Error ? e.message : "ทดสอบไม่สำเร็จ",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Icon-free controls still need names: a screen reader announced
            "combobox" and "textbox" with no indication of what they set. */}
        <select
          value={draftProvider}
          disabled={busy}
          aria-label="ผู้ให้บริการ AI"
          onChange={(e) => {
            setDraftProvider(e.target.value);
            setResult(null);
          }}
          className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-sm text-ink-900 disabled:opacity-50"
        >
          {PROVIDERS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={draftModel}
          disabled={busy}
          spellCheck={false}
          aria-label="ชื่อ model"
          list="ai-model-suggestions"
          placeholder="เช่น gemini-2.5-flash"
          onChange={(e) => {
            setDraftModel(e.target.value);
            setResult(null);
          }}
          className="w-64 rounded-md border border-neutral-300 px-2 py-1.5 font-mono text-sm text-ink-900 disabled:opacity-50"
        />
        <datalist id="ai-model-suggestions">
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>

        <button
          type="button"
          onClick={() => void runTest()}
          disabled={busy || testing || !valid}
          className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm text-ink-900 hover:bg-neutral-50 disabled:opacity-50"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : null}
          ทดสอบ
        </button>

        <button
          type="button"
          onClick={() => onSave(draftProvider, draftModel.trim(), result ? result.ok : null)}
          // Also enabled after a test on an unchanged value: pinning the
          // current model explicitly, together with the fact that it was just
          // seen to work, is a real thing to want to save.
          disabled={busy || !valid || (!dirty && !result) || blockedBy !== null}
          className="rounded-md bg-ink-950 px-2.5 py-1.5 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
        >
          {saveLabel}
        </button>

        {onClear && (
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="text-xs text-neutral-700 underline hover:text-ink-900 disabled:opacity-50"
          >
            ใช้ค่าเริ่มต้น
          </button>
        )}
      </div>

      {/* Say what is wrong with the text before the save button is reached for. */}
      {draftModel.trim() !== "" && !valid && (
        <p className="text-xs text-error-700">
          ชื่อ model ต้องเป็นตัวอักษร ตัวเลข จุด ขีด หรือขีดล่างเท่านั้น
        </p>
      )}

      {result && (
        <p
          role="status"
          className={`flex items-center gap-1.5 text-xs ${result.ok ? "text-success-700" : "text-error-700"}`}
        >
          {result.ok ? <Check size={14} /> : <X size={14} />}
          {result.message}
        </p>
      )}

      {/* Only once there is something to save: an untouched row whose saved id
          the chat refuses is marked by the page, not a second time here. */}
      {blockedBy && (dirty || result) && <p className="text-xs text-error-700">{blockedBy}</p>}

      {/* The saved row's own history, distinct from a test just run. Never
          tested is not the same as broken, so it reads as neutral. */}
      {!result && !dirty && model !== "" && (
        <p className="text-xs text-neutral-700">
          {verifiedOk === true
            ? "เคยทดสอบแล้วเรียกได้"
            : verifiedOk === false
              ? "ทดสอบครั้งล่าสุดเรียกไม่ได้"
              : "ยังไม่เคยทดสอบ"}
        </p>
      )}
    </div>
  );
};

export default ModelField;
